if (!customElements.get('component-slider')) {
  class ComponentSlider extends HTMLElement {
    constructor() {
      super();
      this.timer = null;
    }

    connectedCallback() {
      const parentTab = this.closest('.tab-switcher__item');
      if (parentTab && !parentTab.classList.contains('active')) {
        this._tabObserver = new MutationObserver(() => {
          if (parentTab.classList.contains('active')) {
            this._tabObserver.disconnect();
            this._tabObserver = null;
            this.setUpSlider();
          }
        });
        this._tabObserver.observe(parentTab, { attributes: true, attributeFilter: ['class'] });
        return;
      }
      this.setUpSlider();
    }

    disconnectedCallback() {
      if (this._tabObserver) {
        this._tabObserver.disconnect();
        this._tabObserver = null;
      }
      window.removeEventListener('resize', this.boundResize);
      if (this.eventsDeclared) this.removeEventListeners();
    }

    setUpSlider() {
      this.classList.remove('loaded');
      this.slider = this.querySelector('.slider__list');
      this.slides = Array.from(this.querySelectorAll('.slider__item'));
      this.prevBtn = this.querySelector('.slider__button.prev');
      this.nextBtn = this.querySelector('.slider__button.next');
      this.counter = this.querySelector('.slider__counter');
      this.counterType = this.dataset.counter;
      this.buttonsType = this.dataset.buttons;
      this.hasFloatingControls = this.hasAttribute('floating-controls');
      this.forceGridMobile = this.classList.contains('forced-grid-mobile');
      this.forceGridTablet = this.classList.contains('forced-grid-tablet');
      this.forceGridDesktop = this.classList.contains('forced-grid-desktop');
      this.autorotate = this.hasAttribute('autorotate');
      this.autorotateSpeed = +this.dataset.autorotateSpeed;
      this.autorotateButton = this.querySelector('.slider__play');

      this.computedStyles = getComputedStyle(this);
      this.thumbnailsMax = +this.computedStyles.getPropertyValue('--max-thumbnails') || (this.counterType == 'images' ? 4 : 5);

      if (this.counterType === 'numbers' || this.counterType === 'dots' || this.counterType === 'images') {
        this.counter = this.querySelector('.slider__counter__thumbnails');
      }

      this.animationDelay = 700; // In milliseconds

      if (!this.slider) return;

      this.isInfinite = this.hasAttribute('infinite');
      this.isDraggable = this.hasAttribute('draggable');
      this.matchThumbnails = this.hasAttribute('match-thumbnails');

      this.currentIndex = 0;
      this.currentPosition = 0;
      this.totalSlides = this.slides.length;
      this.postUpdateTimeout = null;

      this.computedStyle = getComputedStyle(this)
      this.slideOffset = parseFloat(computePixelValue(this.computedStyle.getPropertyValue('--slide-offset')) || 0) * (0.5 * parseFloat(computePixelValue(this.computedStyle.getPropertyValue('--column-count')) || 0));

      this.declareEvents();

      if (this.isInfinite) {
        this.totalClones = +this.dataset.clones + 1;
        this.setSlidesClones();
      } else {
        this.fullSlides = this.slides;
      }

      this.setUpSlides();
      this.setVisibleSlides();
      this.setUpCounter();
      requestAnimationFrame(() => this.classList.add('loaded'));

      this.navigateToSlide(0, false);
      this.updateControls();

      if (!this.isInfinite) {
        this.updateButtons();
      }

      if (this.autorotate) {
        this.resetAutorotate();
        if (this.autorotateButton) {
          this.autorotateButton.addEventListener('click', () => {
            if (this.hasAttribute('play')) {
              this.pauseAutorotate();
            } else {
              this.playAutorotate();
            }
          })
        }
      }
    }

    declareEvents() {
      this.resizeTimeout = null;
      this.boundResize = this.onResize.bind(this);
      this.boundWheel = debounce(this.onWheel.bind(this), 200);
      this.boundThumbnailScroll = this.onThumbnailsScroll.bind(this);

      this.onScrollTimeout = null;
      this.boundOnScroll = this.onScroll.bind(this);

      this.boundNextClick = this.onNextClick.bind(this);
      this.boundPrevClick = this.onPrevClick.bind(this);

      this.boundTouchStart = this.touchStart.bind(this);

      this.boundMouseDown = this.mouseDown.bind(this);
      this.boundMouseMove = this.mouseMove.bind(this);
      this.boundMouseUp = this.mouseUp.bind(this);

      this.boundOnMoveStop = this.onMoveStop.bind(this)

      // Touch and mouse events for dragging
      this.isDragging = false;
      this.hasMoved = false;
      this.startPos = 0;
      this.currentSlide = 0;
      this.prevSlide = 0;
      this.isNavigating = false;

      window.addEventListener('resize', this.boundResize);

      if (!this.eventsDeclared) this.setEventListeners();
    }

    setEventListeners() {
      if (this.eventsDeclared) return;

      if (this.nextBtn) this.nextBtn.addEventListener('click', this.boundNextClick, { passive: false });
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.boundPrevClick, { passive: false });

      this.slider.addEventListener('mousedown', this.boundMouseDown, { passive: false });
      this.slider.addEventListener('touchstart', this.boundTouchStart, { passive: false });
      this.slider.addEventListener('wheel', this.boundWheel, { passive: true });

      this.eventsDeclared = true;
      this.slider.classList.remove('no-events');
    }

    removeEventListeners() {
      if (!this.eventsDeclared) return;

      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.boundNextClick);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.boundPrevClick);

      this.slider.removeEventListener('mousedown', this.boundTouchStart);
      this.slider.removeEventListener('touchstart', this.boundTouchStart);
      this.slider.removeEventListener('wheel', this.boundWheel, { passive: true });

      this.eventsDeclared = false;
      this.slider.classList.add('no-events');
    }

    onNextClick() {
      this.navigateToSlide(this.currentIndex + 1, true)
    }

    onPrevClick() {
      this.navigateToSlide(this.currentIndex - 1, true)
    }

    navigateToSlide(index, setTransition = true, trigger = null) {
      this.isNavigating = true;
      this.setButtonsDisabled(true);
      if (this.isInfinite) {
        if (this.currentIndex == 0 && index == this.totalSlides - 1 && trigger == 'thumbnail' && this.filteredSlides.length > 2) {
          this.currentIndex = -1;
        } else if (index == 0 && this.currentIndex == this.totalSlides - 1 && trigger == 'thumbnail' && this.filteredSlides.length > 2) {
          this.currentIndex = this.totalSlides;
        } else {
          this.currentIndex = index;
        }
      } else {
        if (this.currentIndex >= this.totalSlides) {
          this.currentIndex = this.totalSlides - 1;
        } else if (this.currentIndex < 0) {
          this.currentIndex = 0;
        } else {
          this.currentIndex = index;
        }
      }

      const isInfiniteOverflow = this.isInfinite && (this.currentIndex < 0 || this.currentIndex >= this.totalSlides);
      this.updateSliderPosition(setTransition, !isInfiniteOverflow);
      if (this.isInfinite) {
        if (this.currentIndex >= this.totalSlides) {
          setTimeout(() => {
            this.setButtonsDisabled(true)
          }, 1)
          setTimeout(() => {
            this.currentIndex = this.currentIndex - this.totalSlides;
            this.updateSliderPosition(false);
          }, this.animationDelay + 1)
        } else if (this.currentIndex < 0) {
          setTimeout(() => {
            this.setButtonsDisabled(true)
          }, 1)
          setTimeout(() => {
            this.currentIndex = this.totalSlides + this.currentIndex;
            this.updateSliderPosition(false);
          }, this.animationDelay + 1)
        }
      }
    }

    updateSliderPosition(setTransition = true, stopNavigating = true) {
      const targetSlide = this.fullSlides.find(slide => +slide.dataset.index === this.currentIndex)
      if (!targetSlide) return;

      if (this.autorotate) {
        this.resetAutorotate();
      }

      this.currentPosition = targetSlide.offsetLeft - this.slidePositions[0];

      const scrollBehavior = setTransition ? 'smooth' : 'auto';

      this.slider.scrollTo({ left: this.currentPosition, behavior: scrollBehavior });// Restore visibility
      clearTimeout(this.postUpdateTimeout)
      this.postUpdateTimeout = setTimeout(() => {
        this.postUpdateSliderPosition(stopNavigating);
      }, setTransition ? 100 : 0)
    }

    postUpdateSliderPosition(stopNavigating = true) {
      this.setButtonsDisabled(false);
      if (this.currentIndex >= 0 && this.currentIndex < this.totalSlides) {
        if (this.counterType == 'numbers' || this.counterType == 'dots' || this.counterType === 'images') requestAnimationFrame(() => { this.updateThumbnails() });
        else if (this.counterType == 'counter') requestAnimationFrame(() => { this.updateCounter() });
      }

      if (!this.isInfinite) requestAnimationFrame(() => { this.updateButtons() });

      this.prevSlide = this.currentPosition;


      if (stopNavigating) this.isNavigating = false;
    }

    navigateToClosestPosition(position) {
      const closestIndex = this.getIndexByClosestPosition(position);

      requestAnimationFrame(() => {
        this.navigateToSlide(closestIndex, true);
      })
    }

    onMoveStop() {
      this.currentPosition = this.slider.scrollLeft;
      if (this.isDraggable) {
        this.navigateToClosestPosition(this.currentPosition);
      } else {

        const movedBy = this.currentPosition - this.prevSlide;
        const nextSlideIndex = this.currentIndex >= this.totalSlides - this.visibleSlides && !this.isInfinite ? this.totalSlides - 1 : this.currentIndex + 1;
        const prevSlideIndex = this.currentIndex === 0 && !this.isInfinite ? 0 : this.currentIndex - 1;
        const nextSlidePosition = +this.filteredFullSlides.find(item => +item.dataset.index === nextSlideIndex)?.dataset.position;
        const prevSlidePosition = +this.filteredFullSlides.find(item => +item.dataset.index === prevSlideIndex)?.dataset.position;

        if (movedBy > 30) {
          if (this.currentPosition < nextSlidePosition)
            this.navigateToSlide(this.currentIndex + 1, true);
          else
            this.navigateToClosestPosition(this.currentPosition);

        } else if (movedBy < -30) {
          if (this.currentPosition > prevSlidePosition)
            this.navigateToSlide(this.currentIndex - 1, true);
          else
            this.navigateToClosestPosition(this.currentPosition);
        } else if (!this.isNavigating) {
          this.navigateToSlide(this.currentIndex)
        }
      }
    }

    getIndexByClosestPosition(position) {
      if (this.scrollablePositions.length == 0) {
        return 0;
      }
      const closestPosition = this.scrollablePositions.reduce((closest, current) => {
        return Math.abs(current - position) < Math.abs(closest - position) ? current : closest
      })
      return +this.scrollableFullSlides.filter(item => +item.dataset.position == closestPosition)[0].dataset.index;

    }

    mouseDown(event) {
      this.isDragging = true;
      this.hasMoved = false;
      this.slider.classList.remove('touch');

      this.startPos = this.getPositionX(event);
      this.startPosY = this.getPositionY(event);
      this.slider.classList.add('grabbing');

      this.slider.addEventListener('mousemove', this.boundMouseMove, { passive: false });
      this.slider.addEventListener('mouseup', this.boundMouseUp, { passive: false });
      this.slider.addEventListener('mouseleave', this.boundMouseUp, { passive: false });
      window.addEventListener('mouseleave', this.boundMouseUp, { passive: false });
      window.addEventListener('mouseup', this.boundMouseUp, { passive: false });
    }

    mouseMove(event) {
      if (!this.isDragging) return;

      const currentX = this.getPositionX(event);
      const currentY = this.getPositionY(event);
      const diffX = Math.abs(currentX - this.startPos);
      const diffY = Math.abs(currentY - this.startPosY);

      if (diffY > diffX) return;
      event.preventDefault();

      this.slider.classList.add('moving');
      this.hasMoved = true;

      const maxSlideOffset = this.isInfinite ? 1 : 0;
      const firstSlidePosition = +this.filteredFullSlides[0].dataset.position - this.slideOffset - this.slidePositions[0];
      const lastSlidePosition = +this.filteredFullSlides[this.filteredFullSlides.length - this.visibleSlides - maxSlideOffset]?.offsetLeft + this.slideOffset;

      let newPos = this.currentPosition + this.startPos - currentX;

      // Clamp the new position within the bounds of the slider
      newPos = Math.max(firstSlidePosition, Math.min(newPos, lastSlidePosition));

      this.slider.scrollLeft = newPos;
    }

    mouseUp(event) {
      // if (event.cancelable) event.preventDefault();

      this.isDragging = false;
      if (this.hasMoved) requestAnimationFrame(this.boundOnMoveStop)

      this.slider.classList.remove('grabbing', 'moving');

      this.slider.removeEventListener('mouseup', this.boundMouseUp, { passive: false });
      this.slider.removeEventListener('mouseleave', this.boundMouseUp, { passive: false });
      this.slider.removeEventListener('mousemove', this.boundMouseMove, { passive: false });
      window.removeEventListener('mouseleave', this.boundMouseUp, { passive: false });
      window.removeEventListener('mouseup', this.boundMouseUp, { passive: false });
    }

    touchStart() {
      this.slider.classList.add('touch');
      this.prevScrollTop = this.slider.scrollTop;
      this.prevScrollLeft = this.slider.scrollLeft;
      this.slider.addEventListener('scroll', this.boundOnScroll, { passive: false });
    }

    getPositionX(event) {
      return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
    }
    getPositionY(event) {
      return event.type.includes('mouse') ? event.pageY : event.touches[0].clientY;
    }

    onWheel() {
      requestAnimationFrame(this.boundOnMoveStop);
    }

    onScroll(event) {
      this.currentIndex = this.getIndexByClosestPosition(this.slider.scrollLeft);
      const diffX = Math.abs(this.slider.scrollLeft - this.prevScrollLeft);
      const diffY = Math.abs(this.slider.scrollTOp - this.prevScrollTop);
      if (diffY > diffX) return;
      event.preventDefault();

      clearTimeout(this.onScrollTimeout);
      this.onScrollTimeout = setTimeout(() => {
        this.postUpdateSliderPosition();

        if (this.isInfinite) {
          if (this.currentIndex >= this.totalSlides) {
            setTimeout(() => {
              this.setButtonsDisabled(true)
            }, 1)
            setTimeout(() => {
              this.currentIndex = this.currentIndex - this.totalSlides;
              this.updateSliderPosition(false);
            }, this.animationDelay + 1)
          } else if (this.currentIndex < 0) {
            setTimeout(() => {
              this.setButtonsDisabled(true)
            }, 1)
            setTimeout(() => {
              this.currentIndex = this.totalSlides + this.currentIndex;
              this.updateSliderPosition(false);
            }, this.animationDelay + 1)
          }
        }

        this.slider.removeEventListener('scroll', this.boundOnScroll, { passive: false });
      }, 200)
    }

    setUpSlides() {
      const widths = Array.from(this.slides, slide => slide.getBoundingClientRect().width);
      let index = 0;
      this.slides.forEach((slide, i) => {
        if (widths[i] > 0) {
          slide.dataset.index = index;
          index++;
        } else {
          slide.removeAttribute('data-index');
        }
      });
      this.totalSlides = index;
      this.hiddenSlides = this.slides.filter((_, i) => widths[i] <= 0);
      this.filteredSlides = this.slides.filter((_, i) => widths[i] > 0);

      this.setClonesData();
      this.setUpSlidesPosition();
    }

    setUpSlidesPosition() {
      this.slideOffset = parseFloat(this.computedStyle.getPropertyValue('--slide-offset') || 0) * (0.5 * parseFloat(this.computedStyle.getPropertyValue('--column-count') || 0));

      this.slidePositions = [];
      const widths = Array.from(this.fullSlides, slide => slide.getBoundingClientRect().width);
      const offsets = Array.from(this.fullSlides, slide => slide.offsetLeft);
      this.fullSlides.forEach((slide, i) => {
        if (widths[i] > 0) {
          slide.dataset.position = offsets[i];
          this.slidePositions.push(offsets[i]);
        } else {
          slide.removeAttribute('data-position');
        }
      });
      this.hiddenFullSlides = this.fullSlides.filter((_, i) => widths[i] <= 0);
      this.filteredFullSlides = this.fullSlides.filter((_, i) => widths[i] > 0);
      this.scrollableFullSlides = [...this.filteredFullSlides];
      this.scrollablePositions = [...this.slidePositions];
      if (!this.isInfinite) {
        for (let i = 1; i < this.visibleSlides; i++) {
          this.scrollableFullSlides.pop();
          this.scrollablePositions.pop();
        }
      }
    }

    setUpCounter() {
      if (this.counterType === 'none') return;

      if (this.counterType === 'dots' || this.counterType === 'numbers' || this.counterType === 'images') {
        this.counter.innerHTML = '';
        const fragment = document.createDocumentFragment();
        this.filteredSlides.forEach((slide, index) => {
          const item = document.createElement('div');
          item.classList.add('thumbnail', `thumbnail--${this.counterType.slice(0, -1)}`);
          if (this.counterType === 'numbers') item.textContent = index + 1;
          if (this.counterType === 'images') item.style.setProperty('--thumbnail-image', `url(${slide.dataset.sliderThumbnailImage})`)
          if (index === 0) item.classList.add('active');
          item.addEventListener('click', () => this.navigateToSlide(index, true, 'thumbnail'));
          fragment.appendChild(item);
        });
        this.counter.appendChild(fragment);

        this.thumbnails = Array.from(this.counter.querySelectorAll('.thumbnail'));
      }

      if (this.counterType === 'counter') {
        this.counterCurrent = this.counter.querySelector('.counter--current');
        this.counterMax = this.counter.querySelector('.counter--max');
        this.updateCounter();
      }
    }

    updateControls() {
      this.classList.remove('hide-clones');
      if (this.counter) this.counter.classList.remove('hidden')
      if (this.counterType == 'dots' || this.counterType == 'numbers' || this.counterType === 'images') {
        this.thumbnails.forEach(thumbnail => {
          thumbnail.classList.remove('hidden')
        })
      }
      if (this.nextBtn) this.nextBtn.classList.remove('hidden');
      if (this.prevBtn) this.prevBtn.classList.remove('hidden');

      if (this.counterType == 'counter') {
        this.updateCounter();
      }

      if ((this.forceGridMobile && window.innerWidth < 750) || (this.forceGridTablet && window.innerWidth >= 750 && window.innerWidth < 990) || (this.forceGridDesktop && window.innerWidth >= 990)) {
        this.removeControls();
        return;
      }

      if (this.counterType == 'dots' || this.counterType == 'numbers' || this.counterType === 'images') {

        const removeDotsLength = (this.isInfinite || this.matchThumbnails) ? 0 : this.visibleSlides - 1;
        const slideWidths = Array.from(this.filteredSlides, slide => slide.getBoundingClientRect().width);
        this.filteredSlides.forEach((slide, index) => {
          if (slideWidths[index] <= 0 || index >= this.filteredSlides.length - removeDotsLength) this.thumbnails[index].classList.add('hidden');
        });

        this.currentThumbnails = this.thumbnails.filter(dot => !dot.classList.contains('hidden'));

        if (this.currentThumbnails.length <= 1 || this.visibleSlides >= this.slides.length) {
          this.removeControls();
        } else {
          if (!this.eventsDeclared) requestAnimationFrame(() => { this.setEventListeners(); })
        }

        if (this.currentThumbnails.length > this.thumbnailsMax) {
          this.counter.classList.add('overflowed');
          this.onThumbnailsScroll();
          this.counter.addEventListener('scroll', this.boundThumbnailScroll, { passive: false });
        } else {
          this.counter.classList.remove('overflowed');
          this.counter.removeEventListener('scroll', this.boundThumbnailScroll);
        }
      } else {
        if (this.visibleSlides >= this.slides.length) {
          this.removeControls();
        } else {
          if (!this.eventsDeclared) requestAnimationFrame(() => { this.setEventListeners(); })
        }
      }
    }

    removeControls() {
      if (this.counter) this.counter.classList.add('hidden');
      if (this.nextBtn) this.nextBtn.classList.add('hidden');
      if (this.prevBtn) this.prevBtn.classList.add('hidden');
      this.classList.add('hide-clones');
      // requestAnimationFrame(() => { this.navigateToSlide(0, false) });
      if (this.eventsDeclared) this.removeEventListeners();
    }

    updateThumbnails() {
      const removeMaxLength = this.isInfinite ? 0 : this.visibleSlides - 1;
      const maxCounterSlides = this.totalSlides - removeMaxLength;

      if (this.currentIndex < 0 || this.currentIndex >= maxCounterSlides) {
        this.currentIndex = Math.max(0, Math.min(this.currentIndex, maxCounterSlides - 1));
        return;
      }

      const currentThumbnail = this.thumbnails[this.currentIndex];
      let lastCurrentThumbnail = currentThumbnail;
      this.thumbnails.forEach((dot, index) => {
        dot.classList.toggle('active', index === this.currentIndex);
        dot.classList.toggle('previous', index === this.currentIndex - 1);
        dot.classList.toggle('next', index === this.currentIndex + 1);

        if (this.matchThumbnails) {
          if (index >= this.currentIndex && index <= this.currentIndex + this.visibleSlides - 1) {
            dot.classList.add('visible-active');
            lastCurrentThumbnail = dot;
          } else {
            dot.classList.remove('visible-active');
          }
        }
      });

      if (!currentThumbnail || !lastCurrentThumbnail) return;

      const counterScrollLeft = this.counter.scrollLeft;
      const counterScrollTop = this.counter.scrollTop;
      const counterClientWidth = this.counter.clientWidth;
      const counterClientHeight = this.counter.clientHeight;
      const currentThumbnailOffsetLeft = currentThumbnail.offsetLeft - this.counter.offsetLeft;
      const lastThumbnailOffsetLeft = lastCurrentThumbnail.offsetLeft - this.counter.offsetLeft;
      const currentThumbnailOffsetTop = lastCurrentThumbnail.offsetTop - this.counter.offsetTop;
      const lastThumbnailOffsetTop = currentThumbnail.offsetTop - this.counter.offsetTop;
      const currentThumbnailWidth = currentThumbnail.getBoundingClientRect().width;
      const currentThumbnailHeight = currentThumbnail.getBoundingClientRect().height;
      const counterGap = parseFloat(computePixelValue(getComputedStyle(this.counter).columnGap) || computePixelValue(getComputedStyle(this.counter).gap) || 0);
      const scrollOffsetX = currentThumbnailWidth + counterGap;
      const scrollOffsetY = currentThumbnailHeight + counterGap;

      let newScrollLeft = counterScrollLeft;
      let newScrollTop = counterScrollTop;
      // Ensure currentThumbnail is fully visible horizontally
      if (currentThumbnailOffsetLeft < counterScrollLeft + scrollOffsetX) {
        newScrollLeft = currentThumbnailOffsetLeft - scrollOffsetX;
      } else if (lastThumbnailOffsetLeft + currentThumbnailWidth > counterScrollLeft + counterClientWidth - scrollOffsetX) {
        newScrollLeft = lastThumbnailOffsetLeft + currentThumbnailWidth - counterClientWidth + scrollOffsetX;
      }

      // Ensure currentThumbnail is fully visible vertically
      if (currentThumbnailOffsetTop < counterScrollTop + scrollOffsetY) {
        newScrollTop = currentThumbnailOffsetTop - scrollOffsetY;
      } else if (lastThumbnailOffsetTop + currentThumbnailHeight > counterScrollTop + counterClientHeight - scrollOffsetY) {
        newScrollTop = lastThumbnailOffsetTop + currentThumbnailHeight - counterClientHeight + scrollOffsetY;
      }

      this.counter.scrollTo({ left: newScrollLeft, top: newScrollTop, behavior: 'smooth' });
    }

    onThumbnailsScroll() {
      requestAnimationFrame(() => {
        const counterScrollLeft = this.counter.scrollLeft;
        const counterOffsetLeft = this.counter.offsetLeft;
        const counterClientWidth = this.counter.clientWidth;
        const thumbnailsOffset = this.hasFloatingControls ? 0 : counterOffsetLeft;

        if (this.thumbnails[0].offsetLeft - thumbnailsOffset < counterScrollLeft) this.counter.classList.add('overflowed--left');
        else this.counter.classList.remove('overflowed--left');

        if (this.thumbnails[this.currentThumbnails.length - 1].offsetLeft - thumbnailsOffset > counterScrollLeft + counterClientWidth) this.counter.classList.add('overflowed--right');
        else this.counter.classList.remove('overflowed--right');

        const counterScrollTop = this.counter.scrollTop;
        const counterOffsetTop = this.counter.offsetTop;
        const counterClientHeight = this.counter.clientHeight;
        const thumbnailsOffsetY = this.hasFloatingControls ? 0 : counterOffsetTop;

        if (this.thumbnails[0].offsetTop - thumbnailsOffsetY < counterScrollTop) this.counter.classList.add('overflowed--top');
        else this.counter.classList.remove('overflowed--top');

        if (this.thumbnails[this.currentThumbnails.length - 1].offsetTop - thumbnailsOffsetY > counterScrollTop + counterClientHeight) this.counter.classList.add('overflowed--bottom');
        else this.counter.classList.remove('overflowed--bottom');
      })
    }

    updateCounter() {
      if (!this.counterCurrent || !this.counterMax) return;

      const removeMaxLength = this.isInfinite ? 0 : this.visibleSlides - 1
      const maxCounterSlides = this.totalSlides - removeMaxLength;
      const currentCounterSlide = this.currentIndex + 1

      if (this.currentIndex < 0) {
        this.currentIndex = 0;
        return;
      }
      if (this.currentIndex > maxCounterSlides - 1) {
        this.currentIndex--;
        return;
      }

      this.counterCurrent.textContent = maxCounterSlides < currentCounterSlide ? maxCounterSlides : currentCounterSlide;
      this.counterMax.textContent = maxCounterSlides;
    }

    updateButtons() {
      if (!this.nextBtn && !this.prevBtn) return;

      const removedItems = this.isInfinite ? 0 : this.visibleSlides - 1;

      if (this.currentIndex > 0) {
        this.prevBtn.removeAttribute('disabled');
        this.prevBtn.removeAttribute('aria-disabled');
        this.prevBtn.classList.remove('disabled');
      }
      else {
        this.prevBtn.setAttribute('disabled', true);
        this.prevBtn.setAttribute('aria-disabled', true);
        this.prevBtn.classList.add('disabled');
      }

      if (this.currentIndex < this.totalSlides - removedItems - 1) {
        this.nextBtn.removeAttribute('disabled');
        this.nextBtn.removeAttribute('aria-disabled');
        this.nextBtn.classList.remove('disabled');
      }
      else {
        this.nextBtn.setAttribute('disabled', true);
        this.nextBtn.setAttribute('aria-disabled', true);
        this.nextBtn.classList.add('disabled');
      }
    }

    setButtonsDisabled(disabled = false) {
      if (!this.nextBtn && !this.prevBtn) return;

      if (disabled) {
        this.prevBtn.setAttribute('disabled', true);
        this.prevBtn.setAttribute('aria-disabled', true);
        this.nextBtn.setAttribute('disabled', true);
        this.nextBtn.setAttribute('aria-disabled', true);
      } else {
        this.prevBtn.removeAttribute('disabled');
        this.prevBtn.removeAttribute('aria-disabled');
        this.nextBtn.removeAttribute('disabled');
        this.nextBtn.removeAttribute('aria-disabled');
      }
    }

    setVisibleSlides() {
      this.visibleSlides = 1;
      if (window.innerWidth < 750) {
        this.visibleSlides = +this.dataset.mobileCol;
      } else if (window.innerWidth < 990) {
        this.visibleSlides = +this.dataset.tabletCol;
      } else {
        this.visibleSlides = +this.dataset.desktopCol;
      }
    }

    setSlidesClones() {

      this.querySelectorAll('.slider__clone').forEach(clone => {
        clone.remove();
      })
      const fragmentEnd = document.createDocumentFragment();
      const fragmentStart = document.createDocumentFragment();
      let prevSlide = null

      for (let i = 0; i <= this.totalClones; i++) {
        if (this.slides[i]) {
          const clone = this.slides[i].cloneNode(true);
          clone.removeAttribute('id');
          clone.classList.replace('slider__item', 'slider__clone');
          clone.classList.add('slider__clone--end');
          fragmentEnd.appendChild(clone)
        }
        if (this.slides[this.slides.length - i - 1]) {
          const clone = this.slides[this.slides.length - i - 1].cloneNode(true);
          clone.removeAttribute('id');
          clone.classList.replace('slider__item', 'slider__clone');
          clone.classList.add('slider__clone--start');
          if (prevSlide != null) fragmentStart.insertBefore(clone, prevSlide);
          else fragmentStart.appendChild(clone);
          prevSlide = clone;
        }
      }

      this.slider.appendChild(fragmentEnd);
      this.slider.insertBefore(fragmentStart, this.slides[0]);

      this.fullSlides = Array.from(this.querySelectorAll('.slider__item, .slider__clone'));
    }

    setClonesData() {
      const endClones = this.fullSlides.filter(slide => slide.classList.contains('slider__clone--end'));
      const endWidths = Array.from(endClones, slide => slide.getBoundingClientRect().width);
      let indexEnd = this.totalSlides;
      endClones.forEach((slide, i) => {
        if (endWidths[i] > 0) {
          slide.dataset.index = indexEnd;
          indexEnd++;
        } else {
          slide.removeAttribute('data-index');
        }
      });

      const startClones = this.fullSlides.filter(slide => slide.classList.contains('slider__clone--start')).reverse();
      const startWidths = Array.from(startClones, slide => slide.getBoundingClientRect().width);
      let indexStart = -1;
      startClones.forEach((slide, i) => {
        if (startWidths[i] > 0) {
          slide.dataset.index = indexStart;
          indexStart--;
        } else {
          slide.removeAttribute('data-index');
        }
      });
    }

    isMobileOrTouchDevice() {
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      const isMobile = mobileRegex.test(navigator.userAgent);

      return hasTouch || isMobile;
    }

    onResize() {
      this.setUpSlides();
      this.setVisibleSlides();
      this.updateControls();
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        this.navigateToClosestPosition(this.currentPosition);
      }, 200)
    }

    setAutorotate() {
      const removeMaxLength = this.isInfinite ? 0 : this.visibleSlides - 1
      const maxCounterSlides = this.totalSlides - removeMaxLength;
      const nextSlide = this.isInfinite ? this.currentIndex + 1 : (this.currentIndex >= maxCounterSlides - 1 ? 0 : this.currentIndex + 1);

      this.navigateToSlide(nextSlide, true)
    }

    resetAutorotate() {
      clearInterval(this.timer);
      this.setAttribute('play', true);
      if (this.autorotateButton) {
        this.autorotateButton.setAttribute('aria-pressed', true);
        this.autorotateButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlider);
      }
      this.remainingTime = this.autorotateSpeed * 1000;
      this.startTimestamp = Date.now();
      this.timer = setInterval(this.setAutorotate.bind(this), this.autorotateSpeed * 1000);
    }

    playAutorotate() {
      this.setAttribute('play', true);
      if (this.autorotateButton) {
        this.autorotateButton.setAttribute('aria-pressed', true);
        this.autorotateButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlider);
      }

      this.timer = setTimeout(() => this.setAutorotate(), this.remainingTime);
    }

    pauseAutorotate() {
      this.removeAttribute('play')
      if (this.autorotateButton) {
        this.autorotateButton.setAttribute('aria-pressed', false);
        this.autorotateButton.setAttribute('aria-label', window.accessibilityStrings.playSlider);
      }
      clearInterval(this.timer);
      if (this.startTimestamp) {
        const elapsed = Date.now() - this.startTimestamp;
        this.remainingTime -= elapsed;
      }
    }

  }

  customElements.define('component-slider', ComponentSlider)
}